/**
 * AIContextSidebar - Contextual AI Assistant Panel
 *
 * Provides AI-powered insights, suggestions, and actions based on current context.
 *
 * @path packages/ui/src/components/AIContextSidebar.tsx
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, Sparkles, Loader2, Link2, ChevronRight, Brain } from 'lucide-react'
import { cn } from '../lib/utils'

// Sub-modules
import { SuggestionCard } from './ai-context-sidebar/SuggestionCard'
import { ChatMessage } from './ai-context-sidebar/ChatMessage'
import { useAIContextSidebar } from './ai-context-sidebar/useAIContextSidebar'
import { getQuickPrompts, getEntityIcon } from './ai-context-sidebar/types'
import type { AIContextSidebarProps, AISuggestion, AIRelatedItem, AIChatMessage } from './ai-context-sidebar/types'

// Re-export types and hook for public API
export type { AIContext, AISuggestion, AIRelatedItem, AIChatMessage, AIContextSidebarProps } from './ai-context-sidebar/types'
export { useAIContextSidebar }

export function AIContextSidebar({ context, isOpen, onClose, apiClient, onAction }: AIContextSidebarProps) {
  const [activeTab, setActiveTab] = useState<'insights' | 'chat' | 'related'>('insights')
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [relatedItems, setRelatedItems] = useState<AIRelatedItem[]>([])
  const [messages, setMessages] = useState<AIChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const quickPrompts = getQuickPrompts(context)

  // Fetch suggestions on context change
  useEffect(() => {
    if (!context.entityId || !apiClient) return
    setIsLoading(true)
    apiClient.post('/api/ai/suggestions/', {
      entity_type: context.entityType, entity_id: context.entityId,
      hub_id: context.hubId, additional_context: context.additionalContext,
    }).then((res) => setSuggestions((res.data || res).suggestions || []))
      .catch(() => setSuggestions([{ id: '1', type: 'insight', title: 'AI Analysis Ready', description: `Analysis for ${context.entityType || 'this page'} is being prepared...`, confidence: 0.85 }]))
      .finally(() => setIsLoading(false))
  }, [context.entityId, context.entityType, apiClient])

  // Fetch related items
  useEffect(() => {
    if (!context.entityId || !apiClient) return
    apiClient.get(`/api/ai/related/?entity_type=${context.entityType}&entity_id=${context.entityId}`)
      .then((res) => setRelatedItems((res.data || res).items || []))
      .catch(() => {})
  }, [context.entityId, context.entityType, apiClient])

  // Scroll to bottom of messages
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  // Send chat message
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return
    const userMsg: AIChatMessage = { id: `user-${Date.now()}`, role: 'user', content: content.trim(), timestamp: new Date() }
    setMessages((prev) => [...prev, userMsg])
    setInputValue('')
    setIsTyping(true)

    try {
      let reply: string
      if (apiClient) {
        const res = await apiClient.post('/api/ai/chat/', {
          message: content, context: { entity_type: context.entityType, entity_id: context.entityId, hub_id: context.hubId, conversation_history: messages.slice(-5).map((m) => ({ role: m.role, content: m.content })) },
        })
        const data = res.data || res
        reply = data.response || data.message || 'I apologize, I could not process that request.'
      } else {
        await new Promise((r) => setTimeout(r, 1000))
        reply = `I understand you're asking about "${content}". As your AI assistant, I can help analyze this ${context.entityType || 'item'} and provide insights. Would you like me to elaborate on any specific aspect?`
      }
      setMessages((prev) => [...prev, { id: `assistant-${Date.now()}`, role: 'assistant', content: reply, timestamp: new Date() }])
    } catch {
      setMessages((prev) => [...prev, { id: `error-${Date.now()}`, role: 'assistant', content: 'I apologize, there was an error processing your request. Please try again.', timestamp: new Date() }])
    } finally {
      setIsTyping(false)
    }
  }, [apiClient, context, messages])

  if (!isOpen) return null

  return (
    <motion.div
      initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed right-0 top-0 bottom-0 w-[400px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-purple-500/10 to-blue-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-white">Quotech AI</h2>
            <p className="text-xs text-slate-500">{context.entityName || context.entityType || 'Contextual Assistant'}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-100 dark:border-slate-800">
        {(['insights', 'chat', 'related'] as const).map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={cn('flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors capitalize', activeTab === tab ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800')}>
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'insights' && (
          <div className="h-full overflow-y-auto p-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12"><div className="flex flex-col items-center gap-3"><Loader2 className="w-8 h-8 text-purple-500 animate-spin" /><p className="text-sm text-slate-500">Analyzing context...</p></div></div>
            ) : suggestions.length > 0 ? (
              <AnimatePresence mode="popLayout">
                {suggestions.map((s) => <SuggestionCard key={s.id} suggestion={s} onAction={onAction} />)}
              </AnimatePresence>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Sparkles className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400 font-medium">No insights yet</p>
                <p className="text-xs text-slate-400 mt-1">Navigate to a job, quote, or inspection to see AI insights</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="space-y-4">
                  <div className="text-center py-6"><Brain className="w-12 h-12 text-purple-400 mx-auto mb-3" /><p className="text-sm text-slate-500">Ask me anything about {context.entityName || 'this page'}</p></div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Quick Prompts</p>
                    {quickPrompts.slice(0, 4).map((p, i) => (
                      <button key={i} onClick={() => sendMessage(p)} className="w-full text-left px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-400 transition-colors">{p}</button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((m) => <ChatMessage key={m.id} message={m} onCopy={(c) => navigator.clipboard.writeText(c)} onFeedback={(id, fb) => onAction?.('chat_feedback', { messageId: id, feedback: fb })} />)}
                  {isTyping && (
                    <div className="flex items-center gap-2 text-slate-400"><div className="flex gap-1"><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} /><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div><span className="text-sm">Thinking...</span></div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-end gap-2">
                <textarea ref={inputRef} value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(inputValue) } }} placeholder="Ask about this page..." className="flex-1 resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500/50 min-h-[44px] max-h-[120px]" rows={1} />
                <button onClick={() => sendMessage(inputValue)} disabled={!inputValue.trim() || isTyping} className={cn('p-3 rounded-xl transition-all', inputValue.trim() && !isTyping ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg hover:shadow-xl' : 'bg-slate-100 dark:bg-slate-800 text-slate-400')}>
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'related' && (
          <div className="h-full overflow-y-auto p-4 space-y-3">
            {relatedItems.length > 0 ? (
              relatedItems.map((item) => {
                const Icon = getEntityIcon(item.type)
                return (
                  <a key={item.id} href={item.url} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors group">
                    <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm"><Icon className="w-5 h-5 text-slate-500" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 dark:text-white truncate">{item.title}</p>{item.subtitle && <p className="text-xs text-slate-500 truncate">{item.subtitle}</p>}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{Math.round(item.relevance * 100)}%</div>
                    <ChevronRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                )
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Link2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400 font-medium">No related items found</p>
                <p className="text-xs text-slate-400 mt-1">Related jobs, quotes, and files will appear here</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-center">
        <p className="text-[10px] text-slate-400">Powered by Quotech AI • Context-aware assistance</p>
      </div>
    </motion.div>
  )
}

export default AIContextSidebar
